import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Radio, Menu, X } from "lucide-react";
import { subscribe as subscribeAudio } from "../lib/audioAnalyzer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Header() {
  const [online, setOnline] = useState(0);
  const [totalVisits, setTotalVisits] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Refs (not state) so the raf-driven bass reads don't cause React
  // re-renders 60 times a second — we mutate the DOM directly instead.
  const logoRef = useRef(null);
  const revolutionRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const o = await axios.get(`${API}/chat/online`);
        setOnline(o.data.count);
      } catch (e) {
        /* silent */
      }
      // Total lifetime visits — read-only stat, backed by the same visit
      // counter that /api/visits/track increments. Refreshed here so the
      // header ticker keeps climbing live for anyone watching. Own endpoint
      // (rather than reading track's response) so we can poll it cheaply
      // without spawning a new geolocation + session update every time.
      try {
        const v = await axios.get(`${API}/visits/total`);
        if (typeof v.data?.total === "number") setTotalVisits(v.data.total);
      } catch (e) {
        /* silent */
      }
    };
    fetchData();
    const t = setInterval(fetchData, 8000);
    return () => clearInterval(t);
  }, []);

  // Sound-reactive logo — the shared audio analyser pushes a normalized
  // bass energy (0..1) each animation frame. We map that to two effects:
  //   • whole logo scales up ~7% on kicks (feels like the amp is pushing)
  //   • "REVOLUTION" glow intensifies + text-shadow flares on drops
  // Everything happens via DOM mutation for zero re-render cost. When
  // audio is idle the effects gracefully return to the resting state
  // because bass stays at 0 and CSS transitions ease back.
  useEffect(() => {
    let lastBass = 0;
    const unsub = subscribeAudio((bass) => {
      // Smooth-attack, slower decay so the pulse feels punchy but not jittery.
      const smoothed = bass > lastBass ? bass : lastBass * 0.85;
      lastBass = smoothed;
      const scale = 1 + smoothed * 0.07;
      const glow = 6 + smoothed * 22; // px
      const alpha = 0.35 + smoothed * 0.65;
      if (logoRef.current) {
        logoRef.current.style.transform = `scale(${scale.toFixed(3)})`;
      }
      if (revolutionRef.current) {
        revolutionRef.current.style.textShadow = `0 0 ${glow.toFixed(1)}px rgba(220,38,38,${alpha.toFixed(3)})`;
      }
    });
    return unsub;
  }, []);

  return (
    <header
      className="sticky top-0 z-40 border-b border-zinc-800 bg-zinc-950/95 backdrop-blur"
      data-testid="site-header"
    >
      <div className="grid grid-cols-12 items-center">
        {/* Logo */}
        <div className="col-span-7 md:col-span-4 border-r border-zinc-800 px-4 md:px-6 py-3 flex items-center gap-3">
          <Radio className="w-5 h-5 text-red-600 shrink-0" strokeWidth={2.5} />
          <Link to="/" className="flex flex-col leading-none" data-testid="site-logo-link">
            <span
              ref={logoRef}
              className="font-display text-lg md:text-2xl glitch whitespace-nowrap origin-left transition-transform duration-75 ease-out will-change-transform"
              data-testid="site-logo"
            >
              DIGITAL{" "}
              <span
                ref={revolutionRef}
                className="text-red-600 transition-[text-shadow] duration-100 ease-out"
              >
                REVOLUTION
              </span>{" "}
              RADIO
            </span>
            <span className="font-mono text-[10px] tracking-[0.4em] text-zinc-300 uppercase mt-1">
              // RADIO · HARD ROCK / HEAVY METAL
            </span>
          </Link>
        </div>

        {/* On Air dot + nav — gets col-span-6 (was 5) so 7 nav links fit
            without overflowing into the listener-count column. `relative
            z-20` keeps its clicks landing on the links even if a link's
            hit-target grazes the sibling column boundary. */}
        <div className="hidden md:flex relative z-20 col-span-6 border-r border-zinc-800 px-4 lg:px-6 py-3 items-center gap-2 lg:gap-3">
          <span className="on-air-dot" aria-hidden />
          <Link
            to="/"
            className="text-xs tracking-[0.25em] text-zinc-100 hover:text-red-500 uppercase whitespace-nowrap"
            data-testid="header-home-link"
          >
            Home
          </Link>
          <Link
            to="/about"
            className="text-xs tracking-[0.25em] text-zinc-100 hover:text-red-500 uppercase border-l border-zinc-800 pl-2 lg:pl-3 whitespace-nowrap"
            data-testid="header-about-link"
          >
            About
          </Link>
          <Link
            to="/djs"
            className="text-xs tracking-[0.25em] text-zinc-100 hover:text-red-500 uppercase border-l border-zinc-800 pl-2 lg:pl-3 whitespace-nowrap"
            data-testid="header-djs-link"
          >
            DJs
          </Link>
          <Link
            to="/schedule"
            className="text-xs tracking-[0.25em] text-zinc-100 hover:text-red-500 uppercase border-l border-zinc-800 pl-2 lg:pl-3 whitespace-nowrap"
            data-testid="header-schedule-link"
          >
            Schedule
          </Link>
          <Link
            to="/archive"
            className="text-xs tracking-[0.25em] text-zinc-100 hover:text-red-500 uppercase border-l border-zinc-800 pl-2 lg:pl-3 whitespace-nowrap"
            data-testid="header-archive-link"
            title="Past shows & interviews on SoundCloud, Mixcloud, YouTube..."
          >
            Archive
          </Link>
          <Link
            to="/directory"
            className="text-xs tracking-[0.25em] text-zinc-100 hover:text-red-500 uppercase border-l border-zinc-800 pl-2 lg:pl-3 whitespace-nowrap"
            data-testid="header-directory-link"
          >
            Directory
          </Link>
          <Link
            to="/family"
            className="text-xs tracking-[0.25em] text-zinc-100 hover:text-red-500 uppercase border-l border-zinc-800 pl-2 lg:pl-3 whitespace-nowrap"
            data-testid="header-family-link"
            title="Our independent artist family — see their gigs"
          >
            Family
          </Link>
          <Link
            to="/partners"
            className="text-xs tracking-[0.25em] text-zinc-100 hover:text-red-500 uppercase border-l border-zinc-800 pl-2 lg:pl-3 whitespace-nowrap"
            data-testid="header-partners-link"
          >
            Partners
          </Link>
          <Link
            to="/submit"
            className="text-xs tracking-[0.25em] text-red-500 hover:text-white uppercase border-l border-red-600/40 pl-2 lg:pl-3 whitespace-nowrap font-display"
            data-testid="header-submit-link"
            title="Bands: submit your music"
          >
            Submit 🎸
          </Link>
        </div>

        {/* Listeners + mobile menu toggle — shrunk to col-span-2 on
            desktop so the nav has room for all 7 links. The "in the pit"
            label is hidden below lg to avoid crowding. */}
        <div className="col-span-5 md:col-span-2 px-3 md:px-4 py-3 flex flex-col items-end justify-center gap-1">
          <div className="flex items-center gap-2 md:gap-3">
            <span className="text-xs tracking-widest text-zinc-300 uppercase hidden lg:inline">
              Live:
            </span>
            <span
              className="font-display text-xl text-red-600"
              data-testid="header-online-count"
            >
              {String(online).padStart(3, "0")}
            </span>
            <span className="text-xs tracking-widest text-zinc-300 uppercase hidden lg:inline">
              in the pit
            </span>
            {/* Hamburger — mobile only. Opens the mobile nav drawer so
                bands / listeners can reach Submit Music, About, DJs etc.
                from any screen size. */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen((o) => !o)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              className="md:hidden ml-1 -mr-1 p-2 border border-zinc-800 hover:border-red-600 text-zinc-100 hover:text-red-500 transition-colors"
              data-testid="header-mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-4 h-4" strokeWidth={2.5} /> : <Menu className="w-4 h-4" strokeWidth={2.5} />}
            </button>
          </div>
          {/* Lifetime visits ticker — climbs every time anyone visits (5min
              dedupe so refresh spam doesn't inflate it). Muted styling so it
              reads as a stat, not a headline. Hidden on very small screens to
              keep the header uncluttered on phones. */}
          {totalVisits > 0 && (
            <div
              className="flex items-center gap-2 text-[10px] md:text-xs tracking-[0.2em] uppercase text-zinc-500"
              data-testid="header-total-visits"
            >
              <span className="hidden sm:inline">Total Visits:</span>
              <span className="sm:hidden">Visits:</span>
              <span className="font-display text-sm md:text-base text-zinc-100 tabular-nums tracking-normal">
                {totalVisits.toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* ===== MOBILE NAV DRAWER ===== */}
      {mobileMenuOpen && (
        <div
          className="md:hidden border-t border-zinc-800 bg-zinc-950"
          data-testid="header-mobile-menu"
        >
          <nav className="flex flex-col divide-y divide-zinc-800">
            {[
              { to: "/", label: "Home", testId: "mobile-nav-home" },
              { to: "/about", label: "About", testId: "mobile-nav-about" },
              { to: "/djs", label: "DJs", testId: "mobile-nav-djs" },
              { to: "/schedule", label: "Schedule", testId: "mobile-nav-schedule" },
              { to: "/archive", label: "Archive", testId: "mobile-nav-archive" },
              { to: "/directory", label: "Directory", testId: "mobile-nav-directory" },
              { to: "/family", label: "Our Family", testId: "mobile-nav-family" },
              { to: "/partners", label: "Partners", testId: "mobile-nav-partners" },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className="px-5 py-3 text-sm tracking-[0.25em] uppercase text-zinc-100 hover:text-red-500 hover:bg-black transition-colors font-display"
                data-testid={link.testId}
              >
                {link.label}
              </Link>
            ))}
            {/* Submit Music — highlighted red so bands find it fast */}
            <Link
              to="/submit"
              onClick={() => setMobileMenuOpen(false)}
              className="px-5 py-4 text-sm tracking-[0.25em] uppercase text-red-500 hover:text-white bg-red-950/30 hover:bg-red-600 transition-colors font-display flex items-center justify-between border-t-2 border-red-600"
              data-testid="mobile-nav-submit"
            >
              <span>🎸 Submit Music</span>
              <span className="text-xs opacity-70">→</span>
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
